import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Form, Select, Table, Tag, Dropdown } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { IconArrowRight } from "@tabler/icons-react";
import PageHeader from "../../components/header/PageHeader";
import { useToast } from "../../components/toast";
import ConfirmModal from "../../components/modal/ConfirmModal";
import api from "../../api";
import ImageUpload from "../../components/image-upload/ImageUpload";

const PRODUCT_TYPE_LABEL = {
  CARD: "Карт",
  LOAN: "Зээл",
  SAVINGS: "Хадгаламж",
  INSURANCE: "Даатгал",
  APARTMENT: "Орон сууц",
};
const PRODUCT_TYPE_COLOR = {
  CARD: "blue",
  LOAN: "orange",
  SAVINGS: "green",
  INSURANCE: "red",
  APARTMENT: "purple",
};
const PRODUCT_TYPE_OPTIONS = [
  { value: "CARD", label: "Карт" },
  { value: "LOAN", label: "Зээл" },
  { value: "SAVINGS", label: "Хадгаламж" },
  { value: "INSURANCE", label: "Даатгал" },
  { value: "APARTMENT", label: "Орон сууц" },
];

// constants
const SUB_TYPE_OPTIONS = {
  CARD: [
    { value: "DEBIT", label: "Дебит" },
    { value: "CREDIT", label: "Кредит" },
  ],
  LOAN: [
    { label: "Хэрэглээний", value: "CONSUMER" },
    { label: "Цалин", value: "SALARY" },
    { label: "Өрхийн", value: "HOUSEHOLD" },
    { label: "Малчин", value: "HERDER" },
    { label: "Машин", value: "CAR" },
    { label: "Лизинг", value: "LEASING" },
    { label: "Тэтгэвэр", value: "PENSION" },
    { label: "Оюутан", value: "STUDENT" },
    { label: "Хадгаламж барьцаалсан", value: "DEPOSIT_BACKED" },
    { label: "Үл хөдлөх барьцаалсан", value: "REAL_ESTATE_BACKED" },
    { label: "Шуурхай зээл", value: "QUICK_LOAN" },
    { label: "Дижитал", value: "DIGITAL" },
    { label: "Бусад", value: "OTHER" },
  ],
  SAVINGS: [
    { value: "TERM", label: "Хугацаатай хадгаламж" },
    { value: "DEMAND", label: "Хугацаагүй хадгаламж" },
    { value: "CERTIFICATE", label: "Сертификат" },
  ],
  INSURANCE: [
    { value: "AUTO", label: "Машин" },
    { value: "LIFE", label: "Амьдрал" },
    { value: "PROPERTY", label: "Орон сууц" },
    { value: "HEALTH", label: "Эрүүл мэнд" },
    { value: "TRAVEL", label: "Аялал" },
  ],
  APARTMENT: [
    { value: "MORTGAGE", label: "Ипотек" },
    { value: "APARTMENT", label: "Орон сууц" },
    { value: "HOUSE", label: "Хашаа & байшин" },
    { value: "RENT", label: "Түрээс" },
  ],
};

export default function Categories() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false); // ✅ нэмсэн

  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState(undefined);
  const [page, setPage] = useState(1);

  // ─── Fetch ───────────────────────────────────────────
  const fetchRows = useCallback(async () => {
    setLoading(true);
    const res = await api.categories_list({
      page,
      limit: 10,
      ...(typeFilter && { productType: typeFilter }),
    });
    if (res?.status === 200) {
      setRows(res.data.data);
      setMeta(res.data.meta);
    } else {
      toast("Өгөгдөл авахад алдаа гарлаа", "error");
    }
    setLoading(false);
  }, [page, typeFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // ─── Client search ───────────────────────────────────
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter(
      (r) =>
        r.label?.toLowerCase().includes(qq) ||
        r.slug?.toLowerCase().includes(qq),
    );
  }, [rows, q]);

  // ─── Create ──────────────────────────────────────────
  const onCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);
      const res = await api.categories_create({
        slug: values.slug.trim().toLowerCase(), // ✅ lowercase
        label: values.label.trim(),
        productType: values.productType,
        subType: values.subType,
        imageUrl: values.imageUrl || null,
      });
      if (res?.status === 201) {
        toast("Амжилттай нэмэгдлээ", "success");
        createForm.resetFields();
        setCreateOpen(false);
        fetchRows();
      } else {
        toast(res?.data?.message || "Нэмэхэд алдаа гарлаа", "error");
      }
    } catch {
    } finally {
      setCreating(false);
    }
  };

  // ─── Edit ────────────────────────────────────────────
  const openEdit = (record) => {
    setEditingRow(record);
    editForm.setFieldsValue({
      slug: record.slug,
      label: record.label,
      productType: record.productType,
      subType: record.subType,
      imageUrl: record.imageUrl || null,
    });
    setEditOpen(true);
  };

  const onUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      setUpdating(true);
      const res = await api.categories_update(editingRow.id, {
        slug: values.slug.trim().toLowerCase(), // ✅ lowercase
        label: values.label.trim(),
        productType: values.productType,
        subType: values.subType,
        imageUrl: values.imageUrl || null,
      });
      if (res?.status === 200) {
        toast("Амжилттай засагдлаа", "success");
        setRows((prev) =>
          prev.map((r) => (r.id === editingRow.id ? { ...r, ...res.data } : r)),
        );
        setEditOpen(false);
        setEditingRow(null);
      } else {
        toast(res?.data?.message || "Засахад алдаа гарлаа", "error");
      }
    } catch {
    } finally {
      setUpdating(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────
  const onDelete = async () => {
    setDeleting(true); // ✅
    const res = await api.categories_delete(deleteModal.id);
    if (res?.status === 200) {
      toast("Устгагдлаа", "info");
      setRows((prev) => prev.filter((r) => r.id !== deleteModal.id));
    } else {
      toast(res?.data?.message || "Устгахад алдаа гарлаа", "error");
    }
    setDeleting(false); // ✅
    setDeleteModal({ open: false, id: null });
  };

  // ─── Columns ─────────────────────────────────────────
  const columns = [
    {
      title: "Зураг",
      dataIndex: "imageUrl",
      width: 60,
      render: (v) =>
        v ? (
          <img
            src={v}
            style={{ width: 36, height: 36, objectFit: "contain" }}
          />
        ) : (
          <span style={{ color: "#ccc" }}>-</span>
        ),
    },
    {
      title: "Бүтээгдэхүүний төрөл",
      dataIndex: "productType",
      width: 300,
      render: (v) => (
        <Tag color={PRODUCT_TYPE_COLOR[v]}>{PRODUCT_TYPE_LABEL[v] || v}</Tag>
      ),
    },
    {
      title: "Дэд төрөл",
      dataIndex: "subType",
      render: (v) =>
        v ? <Tag>{v}</Tag> : <span style={{ color: "#ccc" }}>—</span>,
    },
    {
      title: "Нэр",
      dataIndex: "label",
      render: (v) => <div className="fw-semibold">{v}</div>,
    },
    // {
    //   title: "Slug",
    //   dataIndex: "slug",
    //   render: (v) => <code style={{ fontSize: 12 }}>{v}</code>,
    // },
    {
      title: "Бүтээгдэхүүний тоо",
      dataIndex: "_count",
      width: 180,
      render: (v) =>
        (v?.cards ?? 0) +
        (v?.loans ?? 0) +
        (v?.savings ?? 0) +
        (v?.insurances ?? 0) +
        (v?.apartments ?? 0),
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      render: (v) => new Date(v).toLocaleDateString("mn-MN"),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: <span style={{ fontSize: 13 }}>Засах</span>,
              },
              {
                key: "delete",
                label: (
                  <span style={{ fontSize: 13, color: "#ff4d4f" }}>Устгах</span>
                ),
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openEdit(record);
              if (key === "delete")
                setDeleteModal({ open: true, id: record.id });
            },
          }}
          trigger={["click"]}
        >
          <Button variant="filled" color="default" style={{ fontSize: 12 }}>
            Үйлдэл <IconArrowRight size={12} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <section className="staff-section">
      <PageHeader
        title="Ангилал"
        sub="Бүтээгдэхүүний ангилалуудын жагсаалт"
        rSide={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields();
              setCreateOpen(true);
            }}
          >
            Нэмэх
          </Button>
        }
      />

      <div className="toolbar">
        <Input
          className="search"
          prefix={<SearchOutlined />}
          placeholder="Нэр / slug хайх..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          allowClear
        />
        <Select
          className="status"
          placeholder="Төрлөөр шүүх"
          allowClear
          value={typeFilter}
          onChange={(v) => {
            setTypeFilter(v);
            setPage(1);
            setRows([]); // ✅ хуучин data цэвэрлэх
          }}
          options={PRODUCT_TYPE_OPTIONS}
        />
      </div>

      <div className="table-wrap">
        <Table
          rowKey="id"
          loading={{ spinning: loading, size: "small" }}
          columns={columns}
          dataSource={filtered}
          pagination={{
            current: meta?.page,
            pageSize: meta?.limit,
            total: meta?.total,
            onChange: (p) => setPage(p),
          }}
        />
      </div>

      {/* Create Modal */}
      <Modal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        okText="Нэмэх"
        cancelText="Болих"
        confirmLoading={creating}
        title="Ангилал нэмэх"
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            label="Бүтээгдэхүүний төрөл"
            name="productType"
            rules={[{ required: true, message: "Төрөл сонгоно уу" }]}
          >
            <Select
              options={PRODUCT_TYPE_OPTIONS}
              onChange={() => createForm.setFieldValue("subType", undefined)} // reset
              allowClear
            />
          </Form.Item>

          {/* Create Modal — subType Select */}
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.productType !== cur.productType}
          >
            {({ getFieldValue }) => {
              const pt = getFieldValue("productType");
              const opts = SUB_TYPE_OPTIONS[pt];
              if (!opts) return null;
              return (
                <Form.Item
                  label="Дэд төрөл"
                  name="subType"
                  rules={[{ required: true, message: "Дэд төрөл сонгоно уу" }]}
                >
                  <Select
                    placeholder="Сонгох"
                    options={opts}
                    onChange={(val) => {
                      // subType сонгоход slug автоматаар үүснэ
                      if (val) {
                        createForm.setFieldValue(
                          "slug",
                          val.toLowerCase().replace(/_/g, "-"),
                        );
                      }
                    }}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          {/* Slug field — автоматаар бөглөгдөх боловч засах боломжтой */}
          <Form.Item
            label="Slug"
            name="slug"
            tooltip="Дэд төрөл сонгоход автоматаар үүснэ. Шаардлагатай бол гараар засаж болно."
            rules={[
              { required: true, message: "Slug оруулна уу" },
              {
                pattern: /^[a-z0-9-]+$/,
                message: "Зөвхөн жижиг латин үсэг, тоо, зураас ашиглана уу",
              },
            ]}
          >
            <Input
              placeholder="subType сонгоход автоматаар бөглөгдөнө"
              onChange={(e) =>
                createForm.setFieldValue(
                  "slug",
                  e.target.value.toLowerCase().replace(/\s+/g, "-"),
                )
              }
            />
          </Form.Item>

          {/* <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.productType !== cur.productType}
          >
            {({ getFieldValue }) => {
              const pt = getFieldValue("productType");
              const opts = SUB_TYPE_OPTIONS[pt];
              if (!opts) return null;
              return (
                <Form.Item
                  label="Дэд төрөл"
                  name="subType"
                  rules={[{ required: true, message: "Дэд төрөл сонгоно уу" }]}
                >
                  <Select placeholder="Сонгох" options={opts} />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            label="Slug"
            name="slug"
            rules={[
              { required: true, message: "Slug оруулна уу" },
              {
                // ✅ pattern validation нэмсэн
                pattern: /^[a-z0-9-]+$/,
                message: "Зөвхөн жижиг латин үсэг, тоо, зураас ашиглана уу",
              },
            ]}
            extra="Жишээ: vip, travel, consumer, savings гэх мэт. Зөвхөн жижиг үсэг ашиглана уу."
          >
            <Input
              placeholder="vip"
              onChange={(
                e, // ✅ auto lowercase
              ) =>
                createForm.setFieldValue(
                  "slug",
                  e.target.value.toLowerCase().replace(/\s+/g, "-"),
                )
              }
            />
          </Form.Item> */}
          <Form.Item
            label="Нэр"
            name="label"
            rules={[{ required: true, message: "Нэрээ оруулна уу" }]}
          >
            <Input placeholder="VIP карт" />
          </Form.Item>
          <Form.Item label="Зураг" name="imageUrl">
            <ImageUpload
              value={createForm.getFieldValue("imageUrl")}
              onChange={(url) => createForm.setFieldValue("imageUrl", url)}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditingRow(null);
        }}
        onOk={onUpdate}
        okText="Хадгалах"
        cancelText="Болих"
        confirmLoading={updating}
        title="Ангилал засах"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="Бүтээгдэхүүний төрөл"
            name="productType"
            rules={[{ required: true, message: "Төрөл сонгоно уу" }]}
          >
            <Select
              options={PRODUCT_TYPE_OPTIONS}
              onChange={() => createForm.setFieldValue("subType", undefined)} // reset
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.productType !== cur.productType}
          >
            {({ getFieldValue }) => {
              const pt = getFieldValue("productType");
              const opts = SUB_TYPE_OPTIONS[pt];
              if (!opts) return null;
              return (
                <Form.Item
                  label="Дэд төрөл"
                  name="subType"
                  rules={[{ required: true, message: "Дэд төрөл сонгоно уу" }]}
                >
                  {/* <Select placeholder="Сонгох" options={opts} /> */}
                  {/* Edit Modal — subType Select */}
                  <Select
                    placeholder="Сонгох"
                    options={opts}
                    onChange={(val) => {
                      if (val) {
                        editForm.setFieldValue(
                          "slug",
                          val.toLowerCase().replace(/_/g, "-"),
                        );
                      }
                    }}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            label="Slug"
            name="slug"
            rules={[
              { required: true, message: "Slug оруулна уу" },
              {
                // ✅ pattern validation нэмсэн
                pattern: /^[a-z0-9-]+$/,
                message: "Зөвхөн жижиг латин үсэг, тоо, зураас ашиглана уу",
              },
            ]}
          >
            <Input
              onChange={(
                e, // ✅ auto lowercase
              ) =>
                editForm.setFieldValue(
                  "slug",
                  e.target.value.toLowerCase().replace(/\s+/g, "-"),
                )
              }
            />
          </Form.Item>
          <Form.Item
            label="Нэр"
            name="label"
            rules={[{ required: true, message: "Нэрээ оруулна уу" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Зураг" name="imageUrl">
            <ImageUpload
              value={createForm.getFieldValue("imageUrl")}
              onChange={(url) => createForm.setFieldValue("imageUrl", url)}
            />
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmModal
        showModal={deleteModal.open}
        setShowModal={(v) => setDeleteModal((prev) => ({ ...prev, open: v }))}
        submitHandler={onDelete}
        loading={deleting} // ✅
        title="Устгах уу?"
        description="Карт холбоотой ангилалыг устгах боломжгүй."
        variant="warning"
      />
    </section>
  );
}
